/**
 * CrmResponseRenderer — Sprint 12 hand-designed renderer for displayType='crm_response'.
 *
 * Per spec §8.5: "Activity-feed layout matching CRM patterns; mention picker;
 * deal context inline".
 *
 * The renderer shows three things:
 *   1. A compose surface (multiline textarea) for the AI-drafted response,
 *      backed by `<EditableField />` via FieldRow so AI-edit + manual-edit +
 *      validation behave consistently with every other approval surface.
 *   2. An activity feed reading from `evidence.activity` (or
 *      `content.activity`) — most-recent first. Each entry carries author,
 *      timestamp, and body so reviewers see the conversational context the
 *      response is replying to.
 *   3. A deal-context strip pinned at the top — deal name, stage, value,
 *      account, owner — pulled from `evidence.deal` (or `content.deal`).
 *
 * Mention picker: the textarea autocompletes when the user types `@` against
 * a candidate list at `evidence.mentions` (or `content.mentions`) — array of
 * `{ id, name, role? }`. If no candidates, mentions still type-through as
 * plain text. This is intentionally minimal — the spec calls for a
 * "mention picker" not a full Slack-style RTE.
 */

import * as React from 'react';
import { MessageSquare, Hash, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { readPath } from '@argusai/shared';
import type { FieldDef } from '@argusai/shared';
import { FieldRow } from '../AutoRenderedApprovalCard';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import type { RendererComponent } from '../AutoRenderedApprovalCard';

// Canonical paths the renderer probes. Authors using the `content.*`
// namespace inherited from email_draft get first-class treatment;
// flat-evidence authors still work via the fallback path.
const CANONICAL_PATHS = {
  responseBody: ['content.response_body', 'response_body'],
  activity: ['content.activity', 'activity'],
  deal: ['content.deal', 'deal'],
  mentions: ['content.mentions', 'mentions'],
} as const;

interface ActivityEntry {
  author: string;
  at: string;
  body: string;
  kind?: string;
}

interface MentionCandidate {
  id: string;
  name: string;
  role?: string;
}

interface Deal {
  name?: string;
  stage?: string;
  account?: string;
  value?: number;
  currency?: string;
  expected_close?: string;
  owner?: string;
}

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function firstResolved<T>(
  evidence: Record<string, unknown>,
  paths: readonly string[],
  coerce: (v: unknown) => T | undefined,
): { value: T; path: string } | { value: undefined; path: string } {
  for (const p of paths) {
    const raw = readPath(evidence, p);
    const v = coerce(raw);
    if (v !== undefined) return { value: v, path: p };
  }
  return { value: undefined, path: paths[0] };
}

function toActivity(raw: unknown): ActivityEntry[] {
  return asArray(raw)
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map((e) => ({
      author: asString(e.author ?? e.from ?? 'unknown'),
      at: asString(e.at ?? e.timestamp ?? e.date ?? ''),
      body: asString(e.body ?? e.text ?? e.message ?? ''),
      kind: asString(e.kind ?? e.type ?? '') || undefined,
    }));
}

function toMentions(raw: unknown): MentionCandidate[] {
  return asArray(raw)
    .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
    .map((m) => ({
      id: asString(m.id ?? m.userId ?? m.username),
      name: asString(m.name ?? m.displayName ?? m.id),
      role: asString(m.role ?? '') || undefined,
    }))
    .filter((m) => m.id !== '' && m.name !== '');
}

function toDeal(raw: unknown): Deal {
  if (!raw || typeof raw !== 'object') return {};
  const d = raw as Record<string, unknown>;
  return {
    name: asString(d.name ?? d.title ?? '') || undefined,
    stage: asString(d.stage ?? d.status ?? '') || undefined,
    account: asString(d.account ?? d.accountName ?? d.account_name ?? '') || undefined,
    value:
      typeof d.value === 'number'
        ? d.value
        : typeof d.amount === 'number'
          ? d.amount
          : undefined,
    currency: asString(d.currency ?? '') || undefined,
    expected_close:
      asString(d.expected_close ?? d.expectedClose ?? d.close_date ?? '') ||
      undefined,
    owner: asString(d.owner ?? d.ownerName ?? '') || undefined,
  };
}

function fmtMoney(n: number | undefined, code = 'USD'): string {
  if (n === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n}`;
  }
}

function findField(fields: FieldDef[], paths: readonly string[]): FieldDef | undefined {
  for (const p of paths) {
    const f = fields.find((x) => x.path === p);
    if (f) return f;
  }
  return undefined;
}

// Lightweight mention dropdown. Renders only when the textarea contains an
// active `@token` (last word starts with `@`) AND we have candidates. Pure
// type-through if either is missing — the user just types `@john` as
// literal text and the workflow author can resolve it server-side.
//
// Keyboard nav (FIX2-FRONTEND defect 3):
//   - ArrowUp / ArrowDown — cycle `selectedIndex`
//   - Enter / Tab          — pick the highlighted candidate
//   - Escape               — close the dropdown
// Keystrokes are wired on the textarea (NOT the popover buttons) because
// the textarea owns focus while the user is composing — putting the
// listener on the popover would require focus theft which interrupts
// typing.
//
// `filtered` is computed by the parent and passed in as a stable array
// so the textarea's keydown handler can advance `selectedIndex` against
// the same list the popover renders.
function MentionPopover({
  filtered,
  selectedIndex,
  onPick,
  anchorRect,
}: {
  filtered: MentionCandidate[];
  selectedIndex: number;
  onPick: (c: MentionCandidate) => void;
  anchorRect: DOMRect | null;
}) {
  if (filtered.length === 0 || !anchorRect) return null;
  // Position below the textarea — simple absolute offset; the parent column
  // is the positioning context so we use top/left in viewport coords.
  const top = anchorRect.bottom + 4;
  const left = anchorRect.left;
  return (
    <div
      role="listbox"
      data-mention-picker="true"
      className="fixed z-50 max-h-56 w-64 overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
      style={{ top, left }}
    >
      {filtered.map((c, i) => {
        const isSelected = i === selectedIndex;
        return (
          <button
            key={c.id}
            type="button"
            role="option"
            aria-selected={isSelected ? 'true' : 'false'}
            data-selected={isSelected ? 'true' : 'false'}
            onMouseDown={(e) => {
              // mouseDown (not click) so the textarea doesn't blur first and
              // tear down the popover before the picker fires.
              e.preventDefault();
              onPick(c);
            }}
            className={
              'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm ' +
              (isSelected ? 'bg-accent' : 'hover:bg-accent')
            }
            data-mention-id={c.id}
          >
            <span className="flex items-center gap-1.5">
              <Hash className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">{c.name}</span>
            </span>
            {c.role && (
              <span className="text-[10px] text-muted-foreground">{c.role}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Filter candidates against the active `@query`. Exported so the parent
 * + tests share one definition. Case-insensitive substring match on name
 * OR id, capped at 6 results to keep the popover scannable.
 */
export function filterMentions(
  candidates: MentionCandidate[],
  query: string,
): MentionCandidate[] {
  const q = query.toLowerCase();
  return candidates
    .filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q),
    )
    .slice(0, 6);
}

// Compose surface — wraps a textarea + mention picker. When a declared
// `editableField` for the response body exists, we still emit edits through
// `onFieldChange(path, …)` so AI-edit + edit-chain integrity stays intact.
//
// FIX2-FRONTEND defect 3 — full keyboard nav on the mention picker:
//   ArrowDown / ArrowUp   move highlight through the filtered list
//   Enter / Tab           insert the highlighted mention (NOT a newline)
//   Escape                close the dropdown without inserting
//
// `selectedIndex` is reset to 0 every time the active query changes so the
// top candidate is always the default — matches Slack / Linear / Notion
// behaviour. When the popover is closed (`mentionQuery === null`), the
// keydown handler short-circuits so the textarea behaves normally.
function ComposeBox({
  value,
  onChange,
  disabled,
  mentions,
  aiBadge,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  mentions: MentionCandidate[];
  aiBadge: boolean;
  label: string;
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null);
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Recompute the filtered list every render — cheap (cap 6) and lets the
  // keydown handler see the same array the popover renders.
  const filtered = React.useMemo(
    () => (mentionQuery === null ? [] : filterMentions(mentions, mentionQuery)),
    [mentions, mentionQuery],
  );

  // Reset highlight to top whenever the query changes (or the popover
  // opens). Clamp on filter shrink so an out-of-range index after the
  // user types more characters snaps to the last valid item.
  React.useEffect(() => {
    if (filtered.length === 0) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((prev) =>
      prev >= filtered.length ? filtered.length - 1 : prev,
    );
  }, [filtered.length]);

  // Compute whether the cursor is inside an `@token` — if yes, surface the
  // mention picker with that token as the filter.
  const onLocalChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    onChange(next);
    const caret = e.target.selectionStart ?? next.length;
    const lead = next.slice(0, caret);
    const m = /(^|\s)@([A-Za-z0-9_\-.]*)$/.exec(lead);
    if (m && mentions.length > 0) {
      const newQuery = m[2] ?? '';
      // Reset highlight on query change so the top of the new filtered
      // list is selected.
      if (newQuery !== mentionQuery) setSelectedIndex(0);
      setMentionQuery(newQuery);
      setAnchorRect(e.target.getBoundingClientRect());
    } else {
      setMentionQuery(null);
      setAnchorRect(null);
    }
  };

  const onPick = React.useCallback(
    (c: MentionCandidate) => {
      const el = textareaRef.current;
      if (!el) return;
      const caret = el.selectionStart ?? value.length;
      const lead = value.slice(0, caret);
      const trail = value.slice(caret);
      const replaced = lead.replace(
        /(^|\s)@([A-Za-z0-9_\-.]*)$/,
        `$1@${c.id} `,
      );
      onChange(replaced + trail);
      setMentionQuery(null);
      setAnchorRect(null);
      setSelectedIndex(0);
      // Re-focus so the user keeps typing without a click.
      requestAnimationFrame(() => {
        el.focus();
        const pos = replaced.length;
        el.setSelectionRange(pos, pos);
      });
    },
    [value, onChange],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Popover not open → behave like a plain textarea.
    if (mentionQuery === null || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % filtered.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      // Pick the highlighted candidate. Tab also closes the popover so
      // the user can keep typing without a tab-out.
      const c = filtered[selectedIndex];
      if (c) {
        e.preventDefault();
        onPick(c);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setMentionQuery(null);
      setAnchorRect(null);
      setSelectedIndex(0);
      return;
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="flex items-center gap-1 text-sm font-medium">
        <span>{label}</span>
        {aiBadge && (
          <Badge
            variant="outline"
            className="ml-1 h-4 px-1 text-[9px] font-semibold uppercase tracking-wider"
            title="This field can be edited with AI"
          >
            AI
          </Badge>
        )}
      </Label>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onLocalChange}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Defer so a mouseDown on the picker still wins the focus race.
          setTimeout(() => {
            setMentionQuery(null);
            setAnchorRect(null);
            setSelectedIndex(0);
          }, 100);
        }}
        disabled={disabled}
        rows={10}
        placeholder="Type a response. Use @ to mention a teammate."
        data-compose-box="true"
        aria-autocomplete="list"
        aria-expanded={mentionQuery !== null && filtered.length > 0}
        aria-activedescendant={
          mentionQuery !== null && filtered.length > 0
            ? `mention-opt-${filtered[selectedIndex]?.id ?? ''}`
            : undefined
        }
      />
      {mentionQuery !== null && (
        <MentionPopover
          filtered={filtered}
          selectedIndex={selectedIndex}
          onPick={onPick}
          anchorRect={anchorRect}
        />
      )}
      <p className="text-xs text-muted-foreground">
        {mentions.length > 0
          ? `${mentions.length} teammate${mentions.length === 1 ? '' : 's'} available to @mention`
          : 'No mention candidates declared — typed @handles pass through as plain text.'}
      </p>
    </div>
  );
}

export const CrmResponseRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  // Resolve canonical paths.
  const responseBody = firstResolved(evidence, CANONICAL_PATHS.responseBody, (v) =>
    typeof v === 'string' ? v : v === undefined ? undefined : asString(v),
  );
  const activity = toActivity(
    firstResolved(evidence, CANONICAL_PATHS.activity, asArray).value,
  );
  const deal = toDeal(
    firstResolved(evidence, CANONICAL_PATHS.deal, (v) =>
      v && typeof v === 'object' ? v : undefined,
    ).value,
  );
  const mentions = toMentions(
    firstResolved(evidence, CANONICAL_PATHS.mentions, asArray).value,
  );

  // Most-recent first.
  const activityFeed = React.useMemo(
    () =>
      [...activity].sort((a, b) => {
        // Empty strings sort to the bottom; otherwise reverse-lex on the
        // ISO-shaped timestamp string (good enough; CRMs ship ISO).
        if (!a.at && !b.at) return 0;
        if (!a.at) return 1;
        if (!b.at) return -1;
        return b.at.localeCompare(a.at);
      }),
    [activity],
  );

  // Resolve which schema field maps to the response body so we know the
  // exact path to emit edits to. Authors who don't declare one get a
  // best-effort fallback to the canonical path.
  const bodyField = findField(schema.editableFields, CANONICAL_PATHS.responseBody);
  const bodyPath = bodyField?.path ?? responseBody.path;
  const bodyValue = (bodyField
    ? asString(readPath(evidence, bodyField.path))
    : asString(responseBody.value)) as string;
  const bodyIsAIEditable = bodyField?.aiEditable === true;

  // Anything declared in editableFields that isn't the body — render below
  // the compose box via FieldRow so authors can still surface CTA fields
  // (e.g., follow_up_date, opp_stage_change) for inline edits.
  const extras = schema.editableFields.filter((f) => f.path !== bodyPath);

  // Pre-compute the dealValue display so we can render `—` cleanly.
  const dealValueDisplay = fmtMoney(deal.value, deal.currency ?? 'USD');

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-4" data-renderer="crm_response">
        {/* Deal context strip */}
        <header className="rounded-md border border-border/60 bg-muted/30 p-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              CRM response
            </span>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
            <DealCell label="Deal" value={deal.name} highlight />
            <DealCell label="Account" value={deal.account} />
            <DealCell label="Stage" value={deal.stage} />
            <DealCell label="Owner" value={deal.owner} />
            <DealCell label="Value" value={dealValueDisplay} />
            {deal.expected_close && (
              <DealCell label="Close" value={deal.expected_close} />
            )}
          </dl>
        </header>

        {/* Compose box */}
        <ComposeBox
          value={bodyValue}
          onChange={(next) => onFieldChange(bodyPath, next)}
          disabled={disabled}
          mentions={mentions}
          aiBadge={bodyIsAIEditable}
          label={bodyField?.label ?? 'Response'}
        />

        {/* Extras the workflow author wants surfaced inline. */}
        {extras.length > 0 && (
          <section className="space-y-3 border-t border-border/60 pt-3">
            {extras.map((f) => (
              <FieldRow
                key={f.path}
                approvalId={approval.id}
                field={f}
                value={readPath(evidence, f.path)}
                onChange={(v) => onFieldChange(f.path, v)}
                disabled={disabled}
              />
            ))}
          </section>
        )}

        {/* Activity feed */}
        <section data-activity-feed="true">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Activity ({activityFeed.length})
          </h3>
          {activityFeed.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/60 p-3 text-center text-xs italic text-muted-foreground">
              No prior activity on this deal.
            </p>
          ) : (
            <ol className="space-y-2">
              {activityFeed.map((entry, i) => (
                <li
                  key={i}
                  className="rounded-md border border-border/60 bg-card p-3 text-sm"
                  data-activity-entry={i}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold">{entry.author}</span>
                    <div className="flex items-center gap-2">
                      {entry.kind && (
                        <Badge variant="outline" className="text-[10px]">
                          {entry.kind}
                        </Badge>
                      )}
                      {entry.at && (
                        <time className="text-[10px] text-muted-foreground">
                          {entry.at}
                        </time>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                    {entry.body}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>

        <ActionBar
          actions={schema.actions}
          approvalId={approval.id}
          onDecide={onDecide}
          disabled={disabled}
          className="rounded-md"
        />
      </div>

      {/* Right rail */}
      <aside className="space-y-3">
        {/* Deal context card — a slimmer repeat of the strip so the context
            stays sticky as the activity feed scrolls. */}
        <section className="rounded-md border border-border/60 bg-card p-3">
          <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Trophy className="h-3 w-3" />
            Deal context
          </h4>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <DealCell label="Stage" value={deal.stage} />
            <DealCell label="Value" value={dealValueDisplay} />
            <DealCell label="Account" value={deal.account} />
            <DealCell label="Owner" value={deal.owner} />
            {deal.expected_close && (
              <DealCell label="Expected close" value={deal.expected_close} />
            )}
          </dl>
        </section>

        {schema.contextPanels.map((panel) => (
          <section
            key={panel.panelId}
            className="rounded-md border border-border/60 bg-card p-3"
          >
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {panel.title}
            </h4>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px]">
              {JSON.stringify(
                panel.dataPath
                  ? readPath(evidence, panel.dataPath)
                  : panel.fields?.length
                    ? Object.fromEntries(
                        panel.fields.map((f) => [
                          f.label,
                          readPath(evidence, f.path) ?? null,
                        ]),
                      )
                    : evidence,
                null,
                2,
              )}
            </pre>
          </section>
        ))}
      </aside>
    </div>
  );
};

function DealCell({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string | undefined;
  highlight?: boolean;
}) {
  return (
    <div className={cn('min-w-0', highlight && 'col-span-2')}>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'truncate font-medium',
          highlight ? 'text-sm' : 'text-xs',
        )}
        title={value}
      >
        {value || '—'}
      </dd>
    </div>
  );
}
